import React, { useState, useEffect } from 'react';
import { apiStatusService, ApiStatus } from '../../services/apiStatusService';
import { useI18n } from '../../contexts/I18nContext';

export const ApiStatusIndicator: React.FC = () => {
  const { formatTime, t } = useI18n();
  const [status, setStatus] = useState<ApiStatus>({
    isConnected: false,
    lastChecked: new Date(),
    error: 'Loading...'
  });
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Polling is gated by subscriber count inside the service — subscribe
    // is enough to start it; the unsubscribe-on-cleanup stops it once no
    // listener remains. The 60s cadence keeps the indicator responsive to
    // outages without piling on traffic against /health.
    apiStatusService.startPeriodicChecks(60000);
    return apiStatusService.subscribe(setStatus);
  }, []);


  const handleManualCheck = async () => {
    await apiStatusService.checkApiHealth();
  };

  const stateClass = status.isConnected ? 'connected' : 'disconnected';
  const statusLabel = status.isConnected
    ? t('apiStatus.connected')
    : t('apiStatus.disconnected');

  return (
    <div className="api-status-indicator">
      <div
        className={`status-badge-container status-badge-container--${stateClass}`}
        onClick={() => setIsExpanded(!isExpanded)}
        title={`API · ${statusLabel}`}
        role="button"
        aria-label={`API · ${statusLabel}`}
      >
        <span className="status-indicator" aria-hidden="true">●</span>
        <span className="status-text">API</span>
      </div>

      {isExpanded && (
        <div className="status-details">
          <div className="status-detail-header">
            <h4>{t('apiStatus.details')}</h4>
            <button
              className="close-btn"
              onClick={() => setIsExpanded(false)}
              title={t('common.close')}
            >
              ✕
            </button>
          </div>

          <div className="status-detail-item">
            <label>Status:</label>
            <span className={`status-value ${status.isConnected ? 'connected' : 'disconnected'}`}>
              {status.isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <div className="status-detail-item">
            <label>Last Check:</label>
            <span className="status-value">
              {formatTime(status.lastChecked)}
            </span>
          </div>

          {status.latency && (
            <div className="status-detail-item">
              <label>Latency:</label>
              <span className="status-value">{status.latency}ms</span>
            </div>
          )}

          {status.serverInfo?.version && (
            <div className="status-detail-item">
              <label>Version:</label>
              <span className="status-value">{status.serverInfo.version}</span>
            </div>
          )}

          {status.error && (
            <div className="status-detail-item">
              <label>Error:</label>
              <span className="status-value error">{status.error}</span>
            </div>
          )}

          <button
            className="manual-check-btn"
            onClick={handleManualCheck}
          >
            Check Now
          </button>
        </div>
      )}
    </div>
  );
};